import type {
  ToServerNoteLinkedData,
  NoteLinkedData,
  NoteLinkedDataNotePart,
  FromServerNoteLinkedData,
} from 'notebook/notes/note-linked-objects/NoteLinkedDataUtil';
import {PersistedPlateMap, PlateMapInput} from 'samples/plate-maps/plate-record/PlateRecordConstants';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';

export interface InitialPlateDesign {
  name: string;
  plateMap: PersistedPlateMap;
  /** A plateDesignInput should be specified if the plate design is being copied.
   *
   * See prepareForSave in NoteLinkedPlateDesignUtil for more details.
   */
  plateDesignInput?: PlateDesignInput;
}

export interface ModifiedPlateDesign {
  plateMap: PlateMap;
  plateDesignInput: PlateDesignInput;
}

export interface PlateDesignInput {
  name: string;
  plateMap: PlateMapInput;
}

export enum PlateDesignStatus {
  INITIAL = 'INITIAL',
  MODIFIED = 'MODIFIED',
}

/**
 * We allow two different shapes for plate designs in {@link NoteLinkedPlateDesign}:
 *
 *  - {@link InitialPlateDesign}:  plate design as it was initially loaded from backend.
 *  - {@link ModifiedPlateDesign}: plate design as it was modified by the user via AnnotateFillPlateModal.
 *
 * Why? Immediately after submitting the modal, we will have access to {@link UIPlateMap}-shaped data,
 * and *not* {@link PlateMap}-shaped data (like we have on initial load). By keeping a reference to this
 * data, we can effortlessly, optimistically render the plate design without waiting for the document save to
 * go through and then refetch the data. The user is also able to re-open the modal even if a save has not yet
 * gone through.
 *
 * There is some risk here by never fetching the plate design from the backend, as the plate map as the user
 * sees may not exactly match up to what is persisted (this is is less of an issue with other note parts
 * since there is usually no distinction between read vs. write shapes).
 *
 * TODO(BNCH-104201): A hybrid strategy could be employed where we visually indicate that the PlateMap is still
 * saving, and upon save, we re-fetch the data from the backend. Note that there is additional work here to
 * provide an API to return the newly persisted plate design and update our front-end component to fetch said
 * data.
 */
export type PlateDesign =
  | {
      status: PlateDesignStatus.INITIAL;
      data: InitialPlateDesign;
    }
  | {
      status: PlateDesignStatus.MODIFIED;
      data: ModifiedPlateDesign;
    };

/**
 * PlateDesignData type from GET.
 *
 * Server representation of the NLO's "data" field.
 * The full NLO is {@link FromServerNoteLinkedPlateDesign}.
 *
 * Keep in sync with PlateDesignNoteLinkedDataGetResponse on backend.
 */
export interface FromServerPlateDesignData {
  id: string;
  versionId: string;
  plateDesign: {
    name: string;
    plateMap: PersistedPlateMap;
  };
  hasPlateRecords: boolean;
}

/**
 * PlateDesignData type for client.
 *
 * Client representation of the NLO's "data" field.
 * The full client NLO is {@link NoteLinkedPlateDesign}.
 *
 * This can be produced by calling NoteLinkedPlateDesignUtil.prepareForStore
 * on a FromServerPlateDesignData.
 */
export interface PlateDesignData {
  id?: undefined;
  cid: string;
  versionId?: undefined;
  versionCid: string;
  plateDesign: PlateDesign;
  hasPlateRecords: boolean;
}

export interface UpdatePlateDesign {
  id: string;
  cid: string;
  versionId: null;
  versionCid: string;
  plateDesign: PlateDesignInput;
}

export interface NewPlateDesign {
  id: null;
  cid: string;
  versionId: null;
  versionCid: string;
  plateDesign: PlateDesignInput;
}

export interface ExistingPlateDesign {
  id: string;
  cid: string;
  versionId: string;
  versionCid: string;
  plateDesign?: never;
}

export type ToServerPlateDesignData = ExistingPlateDesign | NewPlateDesign | UpdatePlateDesign;

/**
 * Type for GET.
 *
 * Keep in sync with PlateDesignNoteLinkedDataGetResponse on backend.
 */
export type FromServerNoteLinkedPlateDesign = FromServerNoteLinkedData<FromServerPlateDesignData>;

/**
 * Type for PATCH.
 *
 * Keep in sync with PlateDesignNoteLinkedDataPatchRequestParams on backend.
 *
 * This can be produced by calling `NoteLinkedPlateDesignUtil.prepareForSave`
 * on a {@link NoteLinkedPlateDesign}.
 */
export type ToServerNoteLinkedPlateDesign = ToServerNoteLinkedData<ToServerPlateDesignData>;

/**
 * Client representation, post-processed after {@link FromServerNoteLinkedPlateDesignData}
 *
 * This can be produced by calling `NoteLinkedPlateDesignUtil.prepareForStore`
 * on a {@link FromServerNoteLinkedPlateDesign}.
 */
export type NoteLinkedPlateDesign = NoteLinkedData<PlateDesignData>;

/**
 * NotePart whose linked item is a plate design
 *
 * This can be produced by calling `prepareNotePartForStore` on a ServerNotePart.
 */
export type NoteLinkedPlateDesignNotePart = NoteLinkedDataNotePart<PlateDesignData>;
